import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { WorkspaceDocument, WorkspaceDocumentType } from 'scribe-web-shared/types/document';
import SortParam from 'v2/components/_workspace/SortParam';
import workspaceService, { WorkspaceFetchArgs } from 'v2/services/workspaceService';

import scribeDocumentService from '../../../services/scribeDocumentService';
import { WorkspacePermission } from '../../types';

export interface WorkspaceState {
  documents: readonly WorkspaceDocument[];
  typeFilter?: WorkspaceDocumentType;
  sortParam?: SortParam;
  userOwnerFilter?: string;
  totalFiles: number;
  loading: boolean;
  loadingMore: boolean;
  category: string;
  selectedDocumentsSection: string;
  isShared?: boolean;
}

export type WorkspaceListResponse = {
  documents: WorkspaceDocument[];
  total_files: number;
  bookmarked_scribes: number;
};

export const setTypeFilter = createAction<WorkspaceDocumentType>('workspace/setTypeFilter');

export const updateSelectedDocumentsSection = createAction<string>('workspace/updateSelectedDocumentsSection');

export const clearTypeFilter = createAction('workspace/clearTypeFilter');

export const setSortParam = createAction<SortParam>('workspace/setSortParam');

export const fetchWorkspace = createAsyncThunk('workspace/fetch', async (query: WorkspaceFetchArgs) => {
  const response = await workspaceService.fetch(query);

  return response as WorkspaceListResponse;
});

export const duplicateScribeFile = createAsyncThunk(
  'workspace/duplicateScribe',
  async (query: { id: string; orgId?: string; retry?: boolean }) => {
    const { id, orgId } = query;

    return (await scribeDocumentService.duplicateScribeDocument(id, orgId)) as WorkspaceDocument;
  }
);

export const retryDuplicateScribeFile = createAsyncThunk('workspace/retryDuplicateScribe', async (query: { id: string }) => {
  const { id } = query;

  return (await scribeDocumentService.retryDuplicateScribeDocument(id)) as WorkspaceDocument;
});

export const fetchMore = createAsyncThunk(
  'workspace/fetchMore',
  async ({ workspacePermission, userId }: { workspacePermission: WorkspacePermission; userId?: string }, thunkAPI) => {
    const folder = (thunkAPI.getState() as any).folders.selectedFolderId;
    const {
      typeFilter,
      userOwnerFilter,
      sortParam,
      category,
      documents: { length: skip },
      isShared
    } = (thunkAPI.getState() as any).workspace as WorkspaceState;
    const response = await workspaceService.fetch({
      folder,
      typeFilter,
      userOwnerFilter: userId || userOwnerFilter,
      sortParam,
      skip,
      category,
      permission: workspacePermission,
      isShared
    });

    return response as WorkspaceListResponse;
  }
);

export const moveFileIntoFolder = createAsyncThunk(
  'workspace/moveFileIntoFolder',
  async ({ fileId, destinationFolderId }: { fileId: string; destinationFolderId: string }) => {
    await workspaceService.changeFolderOrder({ id: fileId, newOrder: 0, newFolderId: destinationFolderId });
    return { fileId, destinationFolderId };
  }
);
